import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { FileText, Users, Building2, LayoutDashboard, LogOut, MessageSquare, CreditCard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { BillingBanner } from "@/components/BillingBanner";

const NAV = [
  { to: "/dashboard", label: "Übersicht", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/invoices", label: "Rechnungen", icon: FileText, id: "nav-invoices" },
  { to: "/customers", label: "Kunden", icon: Users, id: "nav-customers" },
  { to: "/settings", label: "Firmendaten", icon: Building2, id: "nav-settings" },
];
const ABO_NAV = { to: "/abo", label: "Abo", icon: CreditCard, id: "nav-abo" };

export default function Layout() {
  const { user, logout, billing } = useAuth();
  const navigate = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const nav = billing && billing.state !== "free" ? [...NAV, ABO_NAV] : NAV;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#f5f7fb] text-slate-900 flex flex-col antialiased">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img
              src="/fakturaeasy-logo.jpg"
              alt="FakturaEasy"
              data-testid="header-logo"
              className="h-9 w-9 rounded-lg object-contain bg-[#ece6dc] ring-1 ring-slate-200"
            />
            <span className="font-display font-extrabold text-lg tracking-tight">FakturaEasy</span>
          </div>

          <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
            {nav.map(({ to, label, icon: Icon, id }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={id}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-blue-600 text-white" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setFeedbackOpen(true)}
              data-testid="feedback-button"
              title="Feedback geben"
              className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors"
            >
              <MessageSquare className="h-4 w-4" /> Feedback
            </button>
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-medium text-slate-800">{user?.name}</span>
              <span className="text-xs text-slate-400">{user?.email}</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              title="Abmelden"
              className="h-9 w-9 grid place-items-center rounded-lg border border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        <nav className="md:hidden flex items-center gap-1 px-4 pb-2.5 overflow-x-auto">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <BillingBanner />
      </header>

      <main className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 flex-1">
        <Outlet />
      </main>

      {feedbackOpen && <FeedbackDialog onClose={() => setFeedbackOpen(false)} />}
    </div>
  );
}
